import { Line, Point } from "types/canvasTypes";

import { calculateLineLength } from "./util";

export function breakIntoChunksOfGap10(line: Line, scale: number) {
  if (!line.parallelLine) return;

  const lineLength = calculateLineLength(line, scale);
  let numberOfGlass = (line.pannels?.length || 0) + line.numberOfDoors;

  let chunkLength = (lineLength / numberOfGlass) * scale;

  return breakLineIntoChunks(
    line?.parallelLine,
    chunkLength,
    10,
    numberOfGlass
  );
}

export function breakLineIntoChunks(
  line: Line,
  chunkLength: number,
  gapLength: number,
  numberOfGlass: number
): Line[] {
  const { start, end, angle } = line;
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  chunkLength = chunkLength - gapLength;

  const length = Math.sqrt(dx ** 2 + dy ** 2);

  const chunkLines: Line[] = [];
  let currentPosition = 0;

  while (numberOfGlass > 0) {
    const currentLength = chunkLength;

    const chunkStart: Point = {
      x: start.x + (dx / length) * currentPosition,
      y: start.y + (dy / length) * currentPosition,
    };
    const chunkEnd: Point = {
      x: start.x + (dx / length) * (currentPosition + currentLength),
      y: start.y + (dy / length) * (currentPosition + currentLength),
    };

    chunkLines.push({
      start: chunkStart,
      end: chunkEnd,
      angle,
      numberOfDoors: 0,
    });

    currentPosition += currentLength + gapLength;
    numberOfGlass = numberOfGlass - 1;
  }

  return chunkLines;
}

// Constants for canvas styling
const STYLES = {
  MAIN_LINE: {
    color: "black",
    width: 5,
  },
  DOOR_LINE: {
    color: "blue",
    width: 4,
  },
  GLASS_LINE: {
    color: "#add8e6",
    width: 3,
  },
  FONT: {
    MAIN: "20px sans-serif",
    PANEL: "14px sans-serif",
  },
  LENGTH_LABEL: {
    bgColor: "rgba(255, 255, 255, 0.92)",
    borderColor: "rgba(37, 99, 235, 0.35)",
    textColor: "#1d4ed8",
    font: "600 14px sans-serif",
    paddingX: 8,
    paddingY: 5,
    height: 24,
    offsetY: -36,
  },
  PANEL_LABEL: {
    bgColor: "rgba(255, 255, 255, 0.9)",
    borderColor: "rgba(220, 38, 38, 0.35)",
    textColor: "#b91c1c",
    paddingX: 6,
    height: 20,
    offsetY: -10,
  },
} as const;

type LabelBounds = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type SideLengthLabelRegion = {
  lineIndex: number;
  bounds: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

function buildLabelBounds(
  midpointX: number,
  midpointY: number,
  angle: number,
  boxWidth: number,
  boxHeight: number,
  offsetY: number
): LabelBounds {
  const centerOffsetY = offsetY + boxHeight / 2;
  const centerX = midpointX - centerOffsetY * Math.sin(angle);
  const centerY = midpointY + centerOffsetY * Math.cos(angle);

  const cos = Math.abs(Math.cos(angle));
  const sin = Math.abs(Math.sin(angle));
  const aabbWidth = boxWidth * cos + boxHeight * sin;
  const aabbHeight = boxWidth * sin + boxHeight * cos;

  return {
    left: centerX - aabbWidth / 2,
    right: centerX + aabbWidth / 2,
    top: centerY - aabbHeight / 2,
    bottom: centerY + aabbHeight / 2,
  };
}

function isOverlapping(a: LabelBounds, b: LabelBounds, padding = 4) {
  return !(
    a.right + padding < b.left ||
    a.left - padding > b.right ||
    a.bottom + padding < b.top ||
    a.top - padding > b.bottom
  );
}

function reserveLabelOffset({
  midpointX,
  midpointY,
  angle,
  boxWidth,
  boxHeight,
  candidateOffsets,
  occupiedLabelBounds,
}: {
  midpointX: number;
  midpointY: number;
  angle: number;
  boxWidth: number;
  boxHeight: number;
  candidateOffsets: number[];
  occupiedLabelBounds: LabelBounds[];
}) {
  for (const offset of candidateOffsets) {
    const bounds = buildLabelBounds(
      midpointX,
      midpointY,
      angle,
      boxWidth,
      boxHeight,
      offset
    );
    const hasOverlap = occupiedLabelBounds.some((existing) =>
      isOverlapping(bounds, existing)
    );

    if (!hasOverlap) {
      occupiedLabelBounds.push(bounds);
      return offset;
    }
  }

  const fallbackOffset = candidateOffsets[0] ?? 0;
  occupiedLabelBounds.push(
    buildLabelBounds(
      midpointX,
      midpointY,
      angle,
      boxWidth,
      boxHeight,
      fallbackOffset
    )
  );

  return fallbackOffset;
}

function drawMainLine(ctx: CanvasRenderingContext2D, line: Line) {
  ctx.strokeStyle = STYLES.MAIN_LINE.color;
  ctx.lineWidth = STYLES.MAIN_LINE.width;
  ctx.beginPath();
  ctx.moveTo(line.start.x, line.start.y);
  ctx.lineTo(line.end.x, line.end.y);
  ctx.stroke();
}

function drawGlassChunk(
  ctx: CanvasRenderingContext2D,
  chunk: Line,
  index: number,
  totalChunks: number,
  doorsNeeded: number,
  rPanels: any[] | undefined,
  regularPanels: any[] | undefined,
  occupiedLabelBounds: LabelBounds[]
) {
  // Determine chunk style based on position and doors
  if (
    (doorsNeeded === 2 &&
      (index === totalChunks - 3 || index === totalChunks - 2)) ||
    (doorsNeeded === 1 && index === totalChunks - 1)
  ) {
    ctx.strokeStyle = STYLES.DOOR_LINE.color;
    ctx.lineWidth = STYLES.DOOR_LINE.width;
  } else {
    ctx.strokeStyle = STYLES.GLASS_LINE.color;
    ctx.lineWidth = STYLES.GLASS_LINE.width;
  }

  // Draw the chunk
  ctx.beginPath();
  ctx.moveTo(chunk.start.x, chunk.start.y);
  ctx.lineTo(chunk.end.x, chunk.end.y);
  ctx.stroke();

  // Draw panel text
  drawPanelText(
    ctx,
    chunk,
    index,
    totalChunks,
    doorsNeeded,
    rPanels,
    regularPanels,
    occupiedLabelBounds
  );
}

function drawPanelText(
  ctx: CanvasRenderingContext2D,
  chunk: Line,
  index: number,
  totalChunks: number,
  doorsNeeded: number,
  rPanels: any[] | undefined,
  regularPanels: any[] | undefined,
  occupiedLabelBounds: LabelBounds[]
) {
  const dx = chunk.end.x - chunk.start.x;
  const dy = chunk.end.y - chunk.start.y;
  let angle = Math.atan2(dy, dx);

  const midpointX = (chunk.start.x + chunk.end.x) / 2;
  const midpointY = (chunk.start.y + chunk.end.y) / 2;
  if (angle < -Math.PI / 2 || angle > Math.PI / 2) {
    angle += Math.PI;
  }

  ctx.font = STYLES.FONT.PANEL;

  // Determine text content based on position and panel type
  let text = "";
  if (
    doorsNeeded === 2 &&
    rPanels?.length &&
    (index === totalChunks - 1 || index === totalChunks - 4)
  ) {
    text = `${rPanels.pop()?.length}R''`;
  } else if (
    rPanels?.length &&
    index === totalChunks - 2 &&
    doorsNeeded === 1
  ) {
    text = `${rPanels.pop()?.length}R''`;
  } else if (
    (index === totalChunks - 1 && doorsNeeded === 1) ||
    (doorsNeeded === 2 &&
      (index === totalChunks - 2 || index === totalChunks - 3))
  ) {
    text = "36D";
  } else if (regularPanels?.length) {
    text = `${regularPanels.pop()?.length}''`;
  }

  if (text) {
    const textWidth = ctx.measureText(text).width;
    const boxWidth = textWidth + STYLES.PANEL_LABEL.paddingX * 2;
    const boxHeight = STYLES.PANEL_LABEL.height;
    const boxX = -(boxWidth / 2);
    const boxY = reserveLabelOffset({
      midpointX,
      midpointY,
      angle,
      boxWidth,
      boxHeight,
      candidateOffsets: [
        STYLES.PANEL_LABEL.offsetY,
        -STYLES.PANEL_LABEL.offsetY,
        STYLES.PANEL_LABEL.offsetY - 20,
        -STYLES.PANEL_LABEL.offsetY + 20,
      ],
      occupiedLabelBounds,
    });

    ctx.save();
    ctx.translate(midpointX, midpointY);
    ctx.rotate(angle);

    ctx.fillStyle = STYLES.PANEL_LABEL.bgColor;
    ctx.strokeStyle = STYLES.PANEL_LABEL.borderColor;
    ctx.lineWidth = 1;
    ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
    ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

    ctx.fillStyle = STYLES.PANEL_LABEL.textColor;
    ctx.fillText(text, -(textWidth / 2), boxY + boxHeight - 5);
    ctx.restore();
  }
}

function drawLineLength(
  ctx: CanvasRenderingContext2D,
  line: Line,
  lineLength: number,
  occupiedLabelBounds: LabelBounds[],
  lineIndex: number,
  sideLengthLabels: SideLengthLabelRegion[]
) {
  const dx = line.end.x - line.start.x;
  const dy = line.end.y - line.start.y;
  let angle = Math.atan2(dy, dx);

  if (angle < -Math.PI / 2 || angle > Math.PI / 2) {
    angle += Math.PI;
  }

  const midpointX = (line.start.x + line.end.x) / 2;
  const midpointY = (line.start.y + line.end.y) / 2;
  const label = `${lineLength.toFixed(2)}''`;
  ctx.font = STYLES.LENGTH_LABEL.font;
  const textWidth = ctx.measureText(label).width;
  const boxWidth = textWidth + STYLES.LENGTH_LABEL.paddingX * 2;
  const boxHeight = STYLES.LENGTH_LABEL.height;
  const boxX = -(boxWidth / 2);
  const boxY = reserveLabelOffset({
    midpointX,
    midpointY,
    angle,
    boxWidth,
    boxHeight,
    candidateOffsets: [
      STYLES.LENGTH_LABEL.offsetY,
      Math.abs(STYLES.LENGTH_LABEL.offsetY),
      STYLES.LENGTH_LABEL.offsetY - 24,
      Math.abs(STYLES.LENGTH_LABEL.offsetY) + 24,
    ],
    occupiedLabelBounds,
  });
  const labelBounds = buildLabelBounds(
    midpointX,
    midpointY,
    angle,
    boxWidth,
    boxHeight,
    boxY
  );
  sideLengthLabels.push({
    lineIndex,
    bounds: {
      left: labelBounds.left,
      right: labelBounds.right,
      top: labelBounds.top,
      bottom: labelBounds.bottom,
    },
  });

  ctx.save();
  ctx.translate(midpointX, midpointY);
  ctx.rotate(angle);

  ctx.fillStyle = STYLES.LENGTH_LABEL.bgColor;
  ctx.strokeStyle = STYLES.LENGTH_LABEL.borderColor;
  ctx.lineWidth = 1;
  ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
  ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

  ctx.fillStyle = STYLES.LENGTH_LABEL.textColor;
  ctx.fillText(
    label,
    -(textWidth / 2),
    boxY + boxHeight - STYLES.LENGTH_LABEL.paddingY - 2
  );

  ctx.restore();
}

export function redrawCanvas(
  lines: Line[],
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  scale: number,
  viewportZoom: number = 1,
  viewportOffset: Point = { x: 0, y: 0 }
) {
  const sideLengthLabels: SideLengthLabelRegion[] = [];

  // Clear canvas
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  canvas.style.backgroundColor = "#FFFFFF";
  ctx.setTransform(
    viewportZoom,
    0,
    0,
    viewportZoom,
    viewportOffset.x,
    viewportOffset.y
  );

  // Draw all lines
  lines.forEach((line, lineIndex) => {
    const occupiedLabelBounds: LabelBounds[] = [];

    drawMainLine(ctx, line);

    if (!line.parallelLine) return;

    const lineLength = calculateLineLength(line, scale);
    const chunks = breakIntoChunksOfGap10(line, scale);
    if (!chunks) return;

    const rPanels = line.pannels?.filter((panel) => panel.isRPannel);
    const regularPanels = line.pannels?.filter((panel) => !panel.isRPannel);

    // Draw glass chunks
    chunks.forEach((chunk, index) => {
      drawGlassChunk(
        ctx,
        chunk,
        index,
        chunks.length,
        line.numberOfDoors,
        rPanels,
        regularPanels,
        occupiedLabelBounds
      );
    });

    // Draw line length
    drawLineLength(
      ctx,
      line,
      lineLength,
      occupiedLabelBounds,
      lineIndex,
      sideLengthLabels
    );
  });

  ctx.setTransform(1, 0, 0, 1, 0, 0);

  return { sideLengthLabels };
}
